import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  getStructureAdministrative,
  deleteStructureAdmin,
} from "../../services/api";
import { StructureAdministrative } from "../../types/types";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import AdminLayout from "./AdminLayout";

const StructureAdminList: React.FC = () => {
  const [structures, setStructures] = useState<StructureAdministrative[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    fetchStructures();
  }, []);

  const fetchStructures = async () => {
    try {
      setLoading(true);
      const response = await getStructureAdministrative();
      setStructures(response.data);
    } catch (error) {
      console.error("Error fetching structures:", error);
      toast.error("حدث خطأ أثناء جلب الهيكل الإداري");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("هل أنت متأكد من حذف الهيكل الإداري؟")) {
      try {
        await deleteStructureAdmin(id);
        toast.success("تم حذف الهيكل الإداري بنجاح");
        fetchStructures();
      } catch (error) {
        console.error("Error deleting structure:", error);
        toast.error("حدث خطأ أثناء حذف الهيكل الإداري");
      }
    }
  };

  const hasExistingRecord = structures.length > 0;
  const structure = hasExistingRecord ? structures[0] : null;

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-mauritania-green-dark mb-2">
            {getTranslation("adminStructureTitle", language)}
          </h1>
        </div>
        <Link
          to="/admin/structure-admin/new"
          className={`py-2 px-4 rounded transition duration-300 ${
            hasExistingRecord
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-mauritania-green text-white hover:bg-mauritania-green-dark"
          }`}
          onClick={(e) => {
            if (hasExistingRecord) {
              e.preventDefault();
              toast.info("يوجد هيكل إداري بالفعل. يمكنك تعديله فقط.");
            }
          }}
        >
          <FontAwesomeIcon icon={faPlus} className="ml-2" />
          {getTranslation("addStructure", language)}
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">
            {getTranslation("loadingData", language)}
          </p>
        </div>
      ) : !hasExistingRecord ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-600 mb-4">لا يوجد هيكل إداري مضاف حتى الآن</p>
          <Link
            to="/admin/structure-admin/new"
            className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
          >
            <FontAwesomeIcon icon={faPlus} className="ml-2" />
            إضافة أول هيكل إداري
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">
                  العنوان (عربي):
                </h3>
                <p className="text-gray-900">{structure?.titrearabe}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">
                  العنوان (فرنسي):
                </h3>
                <p className="text-gray-900">{structure?.titrefrancais}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">
                  النص (عربي):
                </h3>
                <div
                  className="text-gray-900 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: structure?.textarabe || "",
                  }}
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">
                  النص (فرنسي):
                </h3>
                <div
                  className="text-gray-900 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: structure?.textfrancais || "",
                  }}
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-end">
                <Link
                  to={`/admin/structure-admin/edit/${structure?.id}`}
                  className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300 mr-4"
                >
                  <FontAwesomeIcon icon={faEdit} className="ml-2" />
                </Link>
                <button
                  onClick={() => handleDelete(structure!.id!)}
                  className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-300"
                >
                  <FontAwesomeIcon icon={faTrash} className="ml-2" />
                </button>
              </div>
            </div>
          </div>

          {hasExistingRecord && (
            <div className="p-4 bg-blue-50 border-t border-blue-200"></div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default StructureAdminList;
